import { analyticsApi } from './api'
import { TrackingEvent, EventType, GeoLocation, DeviceInfo, AnalyticsData } from '@/types'

class AnalyticsService {
  private sessionId: string
  private activeIntervals?: Map<string, ReturnType<typeof setInterval>>

  constructor() {
    this.sessionId = this.generateSessionId()
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
  }

  /**
   * Get device information
   */
  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent
    const platform = navigator.platform
    const screenResolution = `${screen.width}x${screen.height}`

    // Detect browser
    let browser = 'Unknown'
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Edge')) browser = 'Edge'

    // Detect device type
    let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop'
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = 'mobile'
    } else if (/iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(userAgent)) {
      deviceType = 'tablet'
    }

    return {
      userAgent,
      platform,
      browser,
      deviceType,
      screenResolution
    }
  }

  /**
   * Get geographic location
   */
  private async getGeographicLocation(): Promise<GeoLocation> {
    try {
      // Try to get location from IP geolocation service
      const response = await fetch('https://ipapi.co/json/')
      if (response.ok) {
        const data = await response.json()
        return {
          country: data.country_name,
          region: data.region,
          city: data.city,
          latitude: data.latitude,
          longitude: data.longitude
        }
      }
    } catch (error) {
      console.error('Failed to get geographic location:', error)
    }

    return {}
  }

  /**
   * Track an event
   */
  async trackEvent(
    contentId: string,
    eventType: EventType,
    additionalData?: any
  ): Promise<void> {
    try {
      const deviceInfo = this.getDeviceInfo()
      const location = await this.getGeographicLocation()

      const event: Omit<TrackingEvent, 'id' | 'timestamp'> = {
        contentId,
        sessionId: this.sessionId,
        eventType,
        location,
        deviceInfo,
        ...additionalData
      }

      await analyticsApi.trackEvent(event)
    } catch (error) {
      console.error('Failed to track event:', error)
    }
  }

  /**
   * Track content view
   */
  async trackView(contentId: string): Promise<void> {
    await this.trackEvent(contentId, 'view')
  }

  /**
   * Track content download
   */
  async trackDownload(contentId: string): Promise<void> {
    await this.trackEvent(contentId, 'download')
  }

  /**
   * Track content share
   */
  async trackShare(contentId: string, platform?: string): Promise<void> {
    await this.trackEvent(contentId, 'share', { platform })
  }

  /**
   * Track content embed
   */
  async trackEmbed(contentId: string, embedUrl?: string): Promise<void> {
    await this.trackEvent(contentId, 'embed', { embedUrl })
  }

  /**
   * Track content play (for media)
   */
  async trackPlay(contentId: string, playTime?: number): Promise<void> {
    await this.trackEvent(contentId, 'play', { playTime })
  }

  /**
   * Get content analytics
   */
  async getContentAnalytics(contentId: string, timeRange?: string): Promise<AnalyticsData> {
    try {
      const response = await analyticsApi.getContentAnalytics(contentId, { timeRange })
      if (response.success) {
        return response.data
      }
      throw new Error(response.error || 'Failed to fetch analytics')
    } catch (error) {
      console.error('Failed to get content analytics:', error)
      throw error
    }
  }

  /**
   * Get tenant analytics
   */
  async getTenantAnalytics(tenantId: string, timeRange?: string): Promise<AnalyticsData> {
    try {
      const response = await analyticsApi.getTenantAnalytics(tenantId, { timeRange })
      if (response.success) {
        return response.data
      }
      throw new Error(response.error || 'Failed to fetch analytics')
    } catch (error) {
      console.error('Failed to get tenant analytics:', error)
      throw error
    }
  }

  /**
   * Get platform analytics (admin only)
   */
  async getPlatformAnalytics(timeRange?: string): Promise<AnalyticsData> {
    try {
      const response = await analyticsApi.getPlatformAnalytics({ timeRange })
      if (response.success) {
        return response.data
      }
      throw new Error(response.error || 'Failed to fetch analytics')
    } catch (error) {
      console.error('Failed to get platform analytics:', error)
      throw error
    }
  }

  /**
   * Start real-time tracking for a content item
   */
  startRealTimeTracking(contentId: string): void {
    // Track initial view
    this.trackView(contentId)

    // Set up periodic tracking for engagement
    const interval = setInterval(() => {
      // Track engagement every 30 seconds
      this.trackEvent(contentId, 'view', { engagement: true })
    }, 30000)

    // Store interval for cleanup
    if (!this.activeIntervals) {
      this.activeIntervals = new Map()
    }
    this.activeIntervals.set(contentId, interval)
  }

  /**
   * Stop real-time tracking for a content item
   */
  stopRealTimeTracking(contentId: string): void {
    if (this.activeIntervals?.has(contentId)) {
      clearInterval(this.activeIntervals.get(contentId))
      this.activeIntervals.delete(contentId)
    }
  }

  /**
   * Clean up all active tracking
   */
  cleanup(): void {
    if (this.activeIntervals) {
      this.activeIntervals.forEach((interval: ReturnType<typeof setInterval>) => clearInterval(interval))
      this.activeIntervals.clear()
    }
  }
}

export const analyticsService = new AnalyticsService()
export default analyticsService 